import React, { useEffect, useState } from "react";
import { PageHeader } from "../components/PageHeader";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import { Plus, Trash, LockKey, Sliders, ShieldCheck } from "@phosphor-icons/react";

const KIND_LABELS = {
  department: "Departments",
  job_role: "Job Roles",
  attendance_status: "Attendance Statuses",
  transaction_category: "Transaction Categories",
  leave_type: "Leave Types",
};

const ENTITIES = [
  { value: "employee", label: "Employee" },
  { value: "report", label: "Daily Report" },
  { value: "transaction", label: "Finance Transaction" },
];

const FIELD_TYPES = [
  { value: "text", label: "Text" },
  { value: "number", label: "Number" },
  { value: "date", label: "Date" },
  { value: "select", label: "Dropdown" },
];

export default function Settings() {
  const { user } = useAuth();
  const [settings, setSettings] = useState({});
  const [newValues, setNewValues] = useState({ department: "", job_role: "", attendance_status: "", transaction_category: "", leave_type: "" });
  const [changePass, setChangePass] = useState({ current_password: "", new_password: "" });
  const [secForm, setSecForm] = useState({ current_password: "", security_question: user?.security_question || "What is your favourite color?", security_answer: "" });

  // Custom fields state
  const [customFields, setCustomFields] = useState([]);
  const [cfForm, setCfForm] = useState({ entity: "employee", label: "", key: "", type: "text", options: "", required: false });

  const load = async () => {
    const [{ data: s }, { data: cf }] = await Promise.all([
      api.get("/settings"),
      api.get("/custom-fields"),
    ]);
    setSettings(s);
    setCustomFields(cf);
  };

  useEffect(() => { load(); }, []);

  const add = async (kind) => {
    const v = newValues[kind].trim();
    if (!v) return;
    try {
      await api.post("/settings", { kind, value: v });
      toast.success("Added");
      setNewValues({ ...newValues, [kind]: "" });
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this entry?")) return;
    await api.delete(`/settings/${id}`);
    toast.success("Deleted"); load();
  };

  const doChangePass = async (e) => {
    e.preventDefault();
    try {
      await api.post("/auth/change-password", changePass);
      toast.success("Password updated");
      setChangePass({ current_password: "", new_password: "" });
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const doUpdateSecurity = async (e) => {
    e.preventDefault();
    try {
      await api.post("/auth/security-question", secForm);
      toast.success("Security question updated");
      setSecForm({ ...secForm, current_password: "", security_answer: "" });
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const addCustomField = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        entity: cfForm.entity,
        label: cfForm.label.trim(),
        key: (cfForm.key || cfForm.label).trim().toLowerCase().replace(/[^a-z0-9]+/g, "_"),
        type: cfForm.type,
        options: cfForm.type === "select"
          ? cfForm.options.split(",").map((s) => s.trim()).filter(Boolean)
          : [],
        required: !!cfForm.required,
      };
      await api.post("/custom-fields", payload);
      toast.success("Custom field created");
      setCfForm({ entity: cfForm.entity, label: "", key: "", type: "text", options: "", required: false });
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const delCustomField = async (id) => {
    if (!window.confirm("Delete this custom field? Existing values will remain in records but no longer appear in forms.")) return;
    await api.delete(`/custom-fields/${id}`);
    toast.success("Deleted"); load();
  };

  const groupedCF = ENTITIES.reduce((acc, e) => {
    acc[e.value] = customFields.filter((c) => c.entity === e.value);
    return acc;
  }, {});

  const isAdmin = user?.role === "admin";

  return (
    <div>
      <PageHeader
        eyebrow="Configuration"
        title="Settings"
        subtitle="Manage dynamic fields, custom fields on any entity, and account security."
      />
      <div className="p-4 sm:p-6 lg:p-8 space-y-8">
        {/* Predefined dynamic fields */}
        <div>
          <div className="label-caps mb-4">Dynamic dropdown lists</div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {Object.keys(KIND_LABELS).map((kind) => (
              <div key={kind} className="bg-white border border-[#E5E7EB] p-6" data-testid={`settings-${kind}`}>
                <div className="label-caps">{KIND_LABELS[kind]}</div>
                <div className="font-display font-bold text-xl mt-1 mb-4">{settings[kind]?.length || 0} entries</div>

                <div className="space-y-2 mb-4 max-h-64 overflow-y-auto">
                  {(settings[kind] || []).map((s) => (
                    <div key={s.id} className="flex items-center justify-between border border-[#E5E7EB] px-3 py-2 text-sm">
                      <span>{s.value}</span>
                      {isAdmin && (
                        <button onClick={() => del(s.id)} className="text-[#DC2626] hover:text-[#991B1B]" data-testid={`del-setting-${s.id}`}>
                          <Trash size={14} />
                        </button>
                      )}
                    </div>
                  ))}
                </div>

                {isAdmin && (
                  <div className="flex gap-2">
                    <input value={newValues[kind]} onChange={(e) => setNewValues({ ...newValues, [kind]: e.target.value })}
                           placeholder={`Add ${KIND_LABELS[kind].toLowerCase().slice(0,-1)}…`} className="flex-1 border border-[#E5E7EB] px-3 py-2 text-sm"
                           data-testid={`add-${kind}-input`} />
                    <button onClick={() => add(kind)} className="brand-btn px-3 py-2 text-sm inline-flex items-center gap-1" data-testid={`add-${kind}-btn`}>
                      <Plus size={14} weight="bold" />
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Custom fields */}
        <div>
          <div className="flex items-center gap-3 mb-4">
            <Sliders size={20} color="#106109" />
            <div>
              <div className="label-caps">Advanced</div>
              <div className="font-display font-bold text-xl">Custom fields on any entity</div>
              <div className="text-xs text-[#6B7280]">Add extra fields that appear in Employee, Daily Report and Transaction forms.</div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {ENTITIES.map((e) => (
              <div key={e.value} className="bg-white border border-[#E5E7EB] p-6" data-testid={`cf-list-${e.value}`}>
                <div className="label-caps">{e.label}</div>
                <div className="font-display font-bold text-xl mt-1 mb-4">{groupedCF[e.value]?.length || 0} custom fields</div>
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {groupedCF[e.value]?.length === 0 && <div className="text-xs text-[#9CA3AF]">No custom fields yet.</div>}
                  {groupedCF[e.value]?.map((c) => (
                    <div key={c.id} className="border border-[#E5E7EB] px-3 py-2 flex items-start justify-between gap-3">
                      <div>
                        <div className="text-sm font-medium">{c.label}{c.required && <span className="text-[#DC2626]"> *</span>}</div>
                        <div className="text-xs text-[#6B7280]">
                          <span className="font-mono">{c.key}</span> · {c.type}
                          {c.type === "select" && c.options?.length ? ` · [${c.options.join(", ")}]` : ""}
                        </div>
                      </div>
                      {isAdmin && (
                        <button onClick={() => delCustomField(c.id)} className="text-[#DC2626]" data-testid={`del-cf-${c.id}`}>
                          <Trash size={14} />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {isAdmin && (
            <div className="mt-6 bg-white border border-[#E5E7EB] p-6" data-testid="cf-create-form">
              <div className="label-caps mb-4">Add a custom field</div>
              <form onSubmit={addCustomField} className="grid grid-cols-1 md:grid-cols-6 gap-3 items-end">
                <div className="md:col-span-1">
                  <label className="label-caps block mb-1.5">Entity</label>
                  <select value={cfForm.entity} onChange={(e) => setCfForm({ ...cfForm, entity: e.target.value })}
                          className="w-full border border-[#E5E7EB] px-3 py-2 text-sm" data-testid="cf-entity">
                    {ENTITIES.map((e) => <option key={e.value} value={e.value}>{e.label}</option>)}
                  </select>
                </div>
                <div className="md:col-span-2">
                  <label className="label-caps block mb-1.5">Label</label>
                  <input required value={cfForm.label} onChange={(e) => setCfForm({ ...cfForm, label: e.target.value })}
                         placeholder="e.g., PAN Number"
                         className="w-full border border-[#E5E7EB] px-3 py-2 text-sm" data-testid="cf-label" />
                </div>
                <div className="md:col-span-1">
                  <label className="label-caps block mb-1.5">Type</label>
                  <select value={cfForm.type} onChange={(e) => setCfForm({ ...cfForm, type: e.target.value })}
                          className="w-full border border-[#E5E7EB] px-3 py-2 text-sm" data-testid="cf-type">
                    {FIELD_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                </div>
                <div className="md:col-span-2">
                  <label className="label-caps block mb-1.5">
                    {cfForm.type === "select" ? "Options (comma-separated)" : "Key (auto if blank)"}
                  </label>
                  {cfForm.type === "select" ? (
                    <input value={cfForm.options} onChange={(e) => setCfForm({ ...cfForm, options: e.target.value })}
                           placeholder="Option A, Option B, Option C"
                           className="w-full border border-[#E5E7EB] px-3 py-2 text-sm" data-testid="cf-options" />
                  ) : (
                    <input value={cfForm.key} onChange={(e) => setCfForm({ ...cfForm, key: e.target.value })}
                           placeholder="pan_number"
                           className="w-full border border-[#E5E7EB] px-3 py-2 text-sm" data-testid="cf-key" />
                  )}
                </div>
                <div className="md:col-span-6 flex items-center gap-6 pt-1">
                  <label className="text-sm flex items-center gap-2">
                    <input type="checkbox" checked={cfForm.required}
                           onChange={(e) => setCfForm({ ...cfForm, required: e.target.checked })}
                           data-testid="cf-required" />
                    Required
                  </label>
                  <button type="submit" className="brand-btn px-4 py-2 text-sm font-semibold inline-flex items-center gap-2" data-testid="cf-submit">
                    <Plus size={14} weight="bold" /> Add custom field
                  </button>
                </div>
              </form>
            </div>
          )}
        </div>

        {/* Change password */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
          <div className="bg-white border border-[#E5E7EB] p-6" data-testid="change-password-card">
            <div className="flex items-center gap-3 mb-4">
              <LockKey size={20} color="#106109" />
              <div>
                <div className="label-caps">Account</div>
                <div className="font-display font-bold text-xl">Change password</div>
              </div>
            </div>
            <form onSubmit={doChangePass} className="space-y-4">
              <div>
                <label className="label-caps block mb-1.5">Current password</label>
                <input type="password" required value={changePass.current_password} onChange={(e) => setChangePass({ ...changePass, current_password: e.target.value })}
                       className="w-full border border-[#E5E7EB] px-3 py-2 text-sm" data-testid="change-current-pass" />
              </div>
              <div>
                <label className="label-caps block mb-1.5">New password</label>
                <input type="password" required minLength={6} value={changePass.new_password} onChange={(e) => setChangePass({ ...changePass, new_password: e.target.value })}
                       className="w-full border border-[#E5E7EB] px-3 py-2 text-sm" data-testid="change-new-pass" />
              </div>
              <button type="submit" className="brand-btn w-full py-2.5 text-sm font-semibold" data-testid="change-pass-submit">Update password</button>
            </form>
          </div>

          <div className="bg-white border border-[#E5E7EB] p-6" data-testid="security-question-card">
            <div className="flex items-center gap-3 mb-4">
              <ShieldCheck size={20} color="#106109" />
              <div>
                <div className="label-caps">Recovery</div>
                <div className="font-display font-bold text-xl">Update security question</div>
                <div className="text-xs text-[#6B7280] mt-0.5">Used on the "Forgot password" flow on the login screen.</div>
              </div>
            </div>
            <form onSubmit={doUpdateSecurity} className="space-y-4">
              <div>
                <label className="label-caps block mb-1.5">Current password (to confirm)</label>
                <input type="password" required value={secForm.current_password}
                       onChange={(e) => setSecForm({ ...secForm, current_password: e.target.value })}
                       className="w-full border border-[#E5E7EB] px-3 py-2 text-sm" data-testid="sec-current-pass" />
              </div>
              <div>
                <label className="label-caps block mb-1.5">Security question</label>
                <input required value={secForm.security_question}
                       onChange={(e) => setSecForm({ ...secForm, security_question: e.target.value })}
                       className="w-full border border-[#E5E7EB] px-3 py-2 text-sm" data-testid="sec-question" />
              </div>
              <div>
                <label className="label-caps block mb-1.5">New answer</label>
                <input required value={secForm.security_answer}
                       onChange={(e) => setSecForm({ ...secForm, security_answer: e.target.value })}
                       className="w-full border border-[#E5E7EB] px-3 py-2 text-sm" data-testid="sec-answer"
                       placeholder="Case-insensitive; kept hashed on the server" />
              </div>
              <button type="submit" className="brand-btn w-full py-2.5 text-sm font-semibold" data-testid="sec-submit">Save security question</button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
